/**
 * Chinese copy for publish gate issues.
 * Paths and reasons map to phone JSON form labels (ops UX).
 */

import { labelForApp, labelForKey } from "./phone-json-labels";

const REASON_LABELS: Record<string, string> = {
  phone_json_missing: "缺少 phone JSON",
  phone_release_missing: "手机发行版缺失",
  media_missing: "媒体缺失",
  media_not_ready: "媒体未就绪",
  "object not registered as ready on R2": "未登记为已就绪素材",
  r2_key_missing: "缺少 R2 键",
  "media URL must be https": "媒体地址须为 https",
  "invalid URL": "无效地址",
  "must be a non-empty array of body blocks": "正文块不能为空",
  "needs at least one paragraph block": "至少需要一个段落块",
  "expected array": "应为数组",
  "expected object": "应为对象",
  "null is not allowed; omit optional fields instead":
    "不能为 null，可选字段请直接省略",
  "bundle must be a JSON object": "案件包必须是 JSON 对象",
  "photos must be an object with media arrays": "相册须为含媒体数组的对象",
  "phone must be an object with calls and/or voicemails":
    "电话须为含通话或语音信箱的对象",
  "maps must be an object with visits": "地图须为含访问记录的对象",
  "safari_bookmarks must be an object with items":
    "Safari 书签须为含条目的对象",
  "safari_bookmarks must include non-empty items": "Safari 书签条目不能为空",
  "must be an array of { body } bubbles": "须为含正文的气泡数组",
  "must be an object": "应为对象",
  "must be an array": "应为数组",
  "must be a non-empty array": "须为非空数组",
  "must be a finite number": "须为有限数字",
  "expected a non-empty string": "须为非空字符串",
  "must be a non-empty string": "须为非空字符串",
  "must hold exactly 3 results": "搜索结果须正好 3 条",
  "must hold exactly 10 history records": "Safari 历史记录须正好 10 条",
  "must hold at least 10 bookmarks": "Safari 书签须至少 10 条",
  "must be a non-negative whole number": "须为非负整数",
};

/**
 * Turn a JSON path into form-facing Chinese labels.
 * e.g. device.apps.safari[6].page.blocks → Safari · 第 7 条 · 页面内容 · 正文块
 */
export function formatPhoneJsonPathLabel(path: string): string {
  const trimmed = path.trim();
  if (!trimmed || trimmed === "(root)") return "根";

  const segments = trimmed.split(".");
  const labels: string[] = [];

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i]!;
    const match = /^([^[\]]+)(?:\[(\d+)\])?$/.exec(segment);
    if (!match) {
      labels.push(segment);
      continue;
    }

    const key = match[1]!;
    const index =
      match[2] != null && match[2] !== "" ? Number(match[2]) : null;

    // Form nav is under Safari / 短信 / …; skip device.apps noise.
    if (key === "device" && segments[i + 1]?.startsWith("apps")) continue;
    if (
      key === "apps" &&
      i > 0 &&
      segments[i - 1] === "device" &&
      segments[i + 1]
    ) {
      continue;
    }

    const label =
      i > 0 && segments[i - 1] === "apps"
        ? labelForApp(key)
        : labelForKey(key);

    if (index != null && Number.isFinite(index)) {
      labels.push(`${label} · 第 ${index + 1} 条`);
    } else {
      labels.push(label);
    }
  }

  return labels.length > 0 ? labels.join(" · ") : trimmed;
}

/** Translate a validate / media / placeholder reason for ops. */
export function formatGateIssueReason(reason: string): string {
  const exact = REASON_LABELS[reason];
  if (exact) return exact;

  const missing = /^missing required field "(.+)"$/.exec(reason);
  if (missing) {
    return `缺少必填「${labelForKey(missing[1]!)}」`;
  }

  const placeholders = /^placeholders_remaining:(\d+)$/.exec(reason);
  if (placeholders) {
    return `仍有 ${placeholders[1]} 个占位符`;
  }

  const unknownPin = /^unknown_phone_pin:(.+)$/.exec(reason);
  if (unknownPin) {
    return `未知手机 pin：${unknownPin[1]}`;
  }

  const unknownKey = /^unknown top-level key "(.+)"$/.exec(reason);
  if (unknownKey) {
    return `未知顶层字段「${labelForKey(unknownKey[1]!)}」`;
  }

  const mustHold = /^must hold exactly (\d+) results$/.exec(reason);
  if (mustHold) {
    return `搜索结果须正好 ${mustHold[1]} 条`;
  }

  const requiredCore =
    /^required core app "(.+)" must be a non-empty array of mock life data$/.exec(
      reason,
    );
  if (requiredCore) {
    return `核心应用「${labelForApp(requiredCore[1]!)}」须为非空数组`;
  }

  const missingR2 = /^r2_object_missing:(.+)$/.exec(reason);
  if (missingR2) {
    return `R2 对象缺失（${missingR2[1]}）`;
  }

  const hostDenied =
    /^host "(.+)" is not in the allowed media host list$/.exec(reason);
  if (hostDenied) {
    return `主机「${hostDenied[1]}」不在允许的媒体域名列表中`;
  }

  return reason.replace(/"([^"]+)"/g, (_all, key: string) => {
    return `「${labelForKey(key)}」`;
  });
}

export function formatReadyGateIssueLine(issue: {
  path: string;
  reason: string;
}): string {
  return `${formatPhoneJsonPathLabel(issue.path)}：${formatGateIssueReason(issue.reason)}`;
}
